import { useEffect, useMemo, useRef, useState } from "react";
import type { ReactNode } from "react";
import { useLocation } from "react-router-dom";

import { useArrangement } from "./ArrangementContext";
import ArrangementNav from "./ArrangementNav";
import BackToReviewButton from "./BackToReviewButton";
import PackageCard from "./PackageCard";
import IncludedUrnStep from "./IncludedUrnStep";
import UrnSelection from "./UrnSelection";
import KeepsakeSelection from "./KeepsakeSelection";
import FingerprintSelection from "./FingerprintSelection";
import AddOnServicesSection from "./AddOnServicesSection";
import PrePlannedNotice from "./PrePlannedNotice";
import { STEP_ROUTES } from "./constants";
import { QUESTION_ID, isFingerprintPreservationIncluded } from "./helpers";
import {
  getPackageSections,
  packageSectionScreenName,
  type PackageSection,
} from "./screenModel";
import type { ArrangementPackage, PlanExternal } from "./types";
import { useInitialSelectedPackageId } from "./useInitialSelectedPackageId";
import { useScreenTracking } from "./useScreenTracking";

/**
 * Headings are regular serif soil, with italic marking an emphasized word and
 * this sans-bold evergreen span carrying the callout. Both are applied to
 * phrases within a heading rather than to the whole line.
 */
const HEADING_CALLOUT = "font-sans font-bold text-evergreen";

const PACKAGE_BADGES: Record<string, string> = {
  Crest: "Most popular",
  Summit: "Most complete",
};

const PACKAGE_COPY: Record<string, Partial<PlanExternal>> = {
  Base: {
    why_choose_detail:
      "For families who are local and prefer to coordinate details themselves. We handle the cremation. You take care of the rest at your own pace.",
    exclusion_highlights: [
      {
        title: "No urn included",
        description: "",
        tooltip: "",
        blurbs: [],
        emphasize: false,
      },
      {
        title: "No death certificates included",
        description: "",
        tooltip: "",
        blurbs: [],
        emphasize: false,
      },
      {
        title: "No delivery of remains included",
        description: "",
        tooltip: "",
        blurbs: [],
        emphasize: false,
      },
    ],
  },
  Crest: {
    why_choose_detail:
      "For families who want the important things handled. Includes a premium urn, delivery of remains, and a death certificate for your records, so you can focus on your family instead of logistics.",
  },
  Summit: {
    why_choose_detail:
      "For families managing from a distance, or anyone who simply wants nothing left to worry about. Includes everything you need for cremation, delivery, estate paperwork, and a keepsake to hold onto.",
  },
};

function PackageGrid({
  packages,
  selectedPackageId,
  initialSelectedPackageId,
  onSelect,
  isReadOnly,
}: {
  packages: ArrangementPackage[];
  selectedPackageId: string | null;
  initialSelectedPackageId: string | null;
  onSelect: (variantId: string) => void;
  isReadOnly: boolean;
}) {
  return (
    // Three across needs roughly 960px of content. Between lg and xl the portal
    // sidebar takes that room back, so the cards stack there as they do on
    // phones.
    <div className="grid grid-cols-1 items-start gap-20 md:grid-cols-3 md:gap-8 lg:grid-cols-1 lg:gap-20 xl:grid-cols-3">
      {packages.map((pkg) => {
        const copy = PACKAGE_COPY[pkg.title] || {};
        const enriched = {
          ...pkg,
          badge: pkg.badge || PACKAGE_BADGES[pkg.title],
          external: {
            ...pkg.external,
            why_choose_detail:
              pkg.external.why_choose_detail || copy.why_choose_detail,
            exclusion_highlights: pkg.external.exclusion_highlights?.length
              ? pkg.external.exclusion_highlights
              : copy.exclusion_highlights || [],
          },
        };
        return (
          <div key={pkg.variant_id}>
            <PackageCard
              pkg={enriched}
              isSelected={selectedPackageId === pkg.variant_id}
              isPreSelected={initialSelectedPackageId === pkg.variant_id}
              onSelect={() => onSelect(pkg.variant_id)}
              hideAction={isReadOnly}
            />
          </div>
        );
      })}
    </div>
  );
}

export default function PackageAndSelectionsStep() {
  const {
    state,
    setSelectedPackage,
    goToStep,
    config,
    isReadOnly,
    packageInclusions,
  } = useArrangement();

  const location = useLocation();

  const initialSelectedPackageId = useInitialSelectedPackageId(
    state.selectedPackageId,
    Boolean(config),
  );

  const hasIncludedUrn = (packageInclusions?.eligibleUrnIds.length ?? 0) > 0;
  const isSingleUrn = (packageInclusions?.eligibleUrnIds.length ?? 0) === 1;

  const packages = useMemo(() => {
    const PACKAGE_ORDER = ["Hospice", "Base", "Crest", "Summit"];
    return [...(config?.packages ?? [])].sort((a, b) => {
      const ai = PACKAGE_ORDER.indexOf(a.title);
      const bi = PACKAGE_ORDER.indexOf(b.title);
      return (
        (ai === -1 ? PACKAGE_ORDER.length : ai) -
        (bi === -1 ? PACKAGE_ORDER.length : bi)
      );
    });
  }, [config?.packages]);

  const planTitle = packages.find(
    (p) => p.variant_id === state.selectedPackageId,
  )?.title;
  // Falls back to the unnamed "your package" so copy never renders "your your
  // package" before a package is selected.
  const planLabel = planTitle ? `your ${planTitle} package` : "your package";

  const sections = useMemo(
    () => getPackageSections(hasIncludedUrn),
    [hasIncludedUrn],
  );

  // Start on the section requested via history state (review "Edit" links, the
  // Services back button), falling back to the first section.
  const [sectionIndex, setSectionIndex] = useState(() => {
    const target = (location.state as { section?: PackageSection } | null)
      ?.section;
    const idx = target ? sections.indexOf(target) : -1;
    return idx >= 0 ? idx : 0;
  });
  // The section list shrinks/grows when the package's included-urn eligibility
  // changes; clamp so we never index past the end.
  const safeIndex = Math.min(sectionIndex, sections.length - 1);
  const section = sections[safeIndex];

  // Exactly one of these renders, and only on the package section outside the
  // care-specialist read-only view. Both spell out every condition rather than
  // leaning on where they sit in the tree, so moving either one cannot make
  // them both render or both vanish.
  const isPreNeedFunded = Boolean(config?.case_context?.is_pre_need_funded);
  const onPackageSection = !isReadOnly && section === "package";
  const showKnownPrePlannedNotice = onPackageSection && isPreNeedFunded;
  const showBroadcastPrePlannedNotice = onPackageSection && !isPreNeedFunded;

  // The mount initializer covers the first render; this applies a requested
  // section when the navigation arrives while the step is already mounted (e.g.
  // the review drawer opened from the packages step). Guarded by location.key
  // so it runs once per navigation. A later sections-list change (switching
  // packages) must not reapply a stale request and pull the family off their
  // current section.
  const appliedSectionNavRef = useRef<string | null>(null);
  useEffect(() => {
    const target = (location.state as { section?: PackageSection } | null)
      ?.section;
    if (!target || appliedSectionNavRef.current === location.key) return;
    appliedSectionNavRef.current = location.key;
    const idx = sections.indexOf(target);
    if (idx >= 0) setSectionIndex(idx);
  }, [location.key, location.state, sections]);

  const { markCompleted } = useScreenTracking({
    screen_name: packageSectionScreenName(section),
    step: "packages",
  });

  // Names of the urns this package covers, in the order they render, so the
  // heading lists them the same way the cards do. A trailing "Urn" is trimmed
  // because some products carry it in their name ("Rosewood Urn"), which would
  // otherwise read as "One Rosewood Urn urn is included".
  const includedUrnLabel = useMemo(() => {
    const eligibleIds = packageInclusions?.eligibleUrnIds ?? [];
    return (config?.urns ?? [])
      .filter((u) => eligibleIds.includes(u.id))
      .map((u) => u.name.replace(/\s+urns?$/i, ""))
      .join(" or ");
  }, [config?.urns, packageInclusions?.eligibleUrnIds]);

  // Fingerprint preservation is included on some plans; when it is, the screen
  // states that rather than asking the family to opt into something they've
  // already paid for.
  const fingerprintIncluded = isFingerprintPreservationIncluded(
    config,
    packageInclusions,
  );

  // The heading carries the ask, the caption carries the why — and where the
  // heading says everything, there is no caption. Optional sections phrase the
  // heading as a question so the wording itself tells the family they may
  // decline. The step name ("Package & Selections") already sits in the
  // progress bar, so no heading repeats it.
  const heading = useMemo((): ReactNode => {
    switch (section) {
      case "package":
        return (
          <>
            <span className={HEADING_CALLOUT}>Choose the package</span> that
            works <span className="italic">for you</span>
          </>
        );
      case "included-urn": {
        // The inclusion is the reassurance the family is here for, so it takes
        // the sans-bold callout; the urn's name takes the italic emphasis.
        const includedPhrase = (
          <span className={HEADING_CALLOUT}>included with {planLabel}</span>
        );
        // Names are omitted if an eligible id has no matching product, so the
        // sentence still reads correctly rather than "One  urn is included".
        return includedUrnLabel ? (
          <>
            One <span className="italic">{includedUrnLabel}</span> urn is{" "}
            {includedPhrase}
          </>
        ) : (
          <>One urn is {includedPhrase}</>
        );
      }
      case "additional-urns":
        // Base includes no urn, so there the ask is for a first urn, not
        // another, and the callout falls on the urn itself instead.
        return hasIncludedUrn ? (
          <>
            Would you like any{" "}
            <span className={HEADING_CALLOUT}>additional urns</span>?
          </>
        ) : (
          <>
            Would you like <span className={HEADING_CALLOUT}>an urn</span> for
            your loved one's remains?
          </>
        );
      case "fingerprints":
        // The included variant takes the same callout as the included-urn
        // heading, since it's the same sentence making the same promise.
        return fingerprintIncluded ? (
          <>
            Fingerprint preservation is{" "}
            <span className={HEADING_CALLOUT}>included with {planLabel}</span>
          </>
        ) : (
          <>
            Would you like us to{" "}
            <span className={HEADING_CALLOUT}>preserve a fingerprint</span>?
          </>
        );
      case "keepsakes":
        return (
          <>
            Would you like any{" "}
            <span className={HEADING_CALLOUT}>keepsakes or jewelry</span>?
          </>
        );
      case "add-ons":
        return (
          <>
            Would you like{" "}
            <span className={HEADING_CALLOUT}>anything else</span> to{" "}
            <span className="italic">honor your loved one</span>?
          </>
        );
    }
  }, [
    section,
    planLabel,
    includedUrnLabel,
    hasIncludedUrn,
    fingerprintIncluded,
  ]);

  const caption = useMemo((): string | undefined => {
    switch (section) {
      case "included-urn":
        return isSingleUrn
          ? undefined
          : "Choose the one that feels right for you.";
      case "fingerprints":
        // Where it's included the print is always captured (ENG-1149), so the
        // caption points at the jewelry below rather than restating the
        // deadline for a decision the family no longer has to make.
        return fingerprintIncluded
          ? "You can have the print set into a piece of jewelry below."
          : "This can only be captured while your loved one is in our care. It cannot be added later.";
      case "keepsakes":
        return "These items allow you to place a small portion of your loved one's ashes inside.";
      case "package":
      case "additional-urns":
      case "add-ons":
        return undefined;
    }
  }, [section, isSingleUrn, fingerprintIncluded]);

  const isFpYes =
    state.serviceAnswers.find((a) => a.questionId === QUESTION_ID.FINGERPRINTS)
      ?.selectedOptionId === "yes";

  // Fine print rendered directly above the Continue button for the current
  // section; add a case here to give another section a hint.
  const continueHint = useMemo(() => {
    switch (section) {
      case "fingerprints":
        return isFpYes
          ? "In the rare event we're unable to obtain a usable fingerprint, your care specialist will contact you promptly and you will not be charged for this item."
          : undefined;
      default:
        return undefined;
    }
  }, [section, isFpYes]);

  const handleBack = () => {
    if (safeIndex > 0) setSectionIndex(safeIndex - 1);
  };

  const canContinue = (() => {
    if (section === "package") return !!state.selectedPackageId;
    if (section === "included-urn")
      return !hasIncludedUrn || state.includedUrnId !== null;
    return true;
  })();

  const handleContinue = () => {
    markCompleted();
    if (safeIndex < sections.length - 1) {
      setSectionIndex(safeIndex + 1);
    } else {
      goToStep(STEP_ROUTES.indexOf("services"));
    }
  };

  return (
    <div className="pb-24">
      <div className="mb-8 text-center">
        <h1 className="font-serif text-4xl text-soil">{heading}</h1>
        {caption && (
          <p className="mx-auto mt-3 text-base text-soil">{caption}</p>
        )}

        {!isReadOnly && (
          <div className="mx-auto mt-4 flex items-center justify-center gap-1.5">
            {sections.map((sv, i) => (
              <span
                key={sv}
                className={`h-1.5 w-8 rounded-full ${
                  i < safeIndex
                    ? "bg-sage"
                    : i === safeIndex
                      ? "bg-evergreen"
                      : "bg-oat"
                }`}
              />
            ))}
          </div>
        )}

        {showKnownPrePlannedNotice && <PrePlannedNotice known />}
      </div>

      {isReadOnly ? (
        <div className="space-y-10">
          <PackageGrid
            packages={packages}
            selectedPackageId={state.selectedPackageId}
            initialSelectedPackageId={initialSelectedPackageId}
            onSelect={setSelectedPackage}
            isReadOnly
          />
          {hasIncludedUrn && <IncludedUrnStep />}
          <UrnSelection />
          <FingerprintSelection />
          <KeepsakeSelection />
          <AddOnServicesSection readOnly={isReadOnly} />
          <BackToReviewButton />
        </div>
      ) : (
        <>
          {section === "package" && (
            <>
              <PackageGrid
                packages={packages}
                selectedPackageId={state.selectedPackageId}
                initialSelectedPackageId={initialSelectedPackageId}
                onSelect={setSelectedPackage}
                isReadOnly={false}
              />
              {showBroadcastPrePlannedNotice && <PrePlannedNotice />}
            </>
          )}
          {section === "included-urn" && <IncludedUrnStep />}
          {section === "additional-urns" && <UrnSelection />}
          {section === "fingerprints" && <FingerprintSelection />}
          {section === "keepsakes" && <KeepsakeSelection />}
          {section === "add-ons" && <AddOnServicesSection />}

          <ArrangementNav
            showBack={safeIndex > 0}
            onBack={handleBack}
            onContinue={handleContinue}
            disableContinue={!canContinue}
            continueHint={
              continueHint && (
                <span className="text-sm text-soil/70">{continueHint}</span>
              )
            }
          />
        </>
      )}
    </div>
  );
}
